import React, { useState, useEffect } from 'react';
import { Clock, Edit3, Save, X, Plus, Trash2 } from 'lucide-react';
import { getRecentUpdates, saveRecentUpdates } from '../../services/homepageContentService';

const RecentUpdatesCard = ({ isAdmin, setMessage }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updates, setUpdates] = useState([]);

  const [newUpdate, setNewUpdate] = useState({
    app: '',
    version: '',
    date: new Date().toISOString().split('T')[0],
    changes: ['']
  });

  useEffect(() => {
    const loadRecentUpdates = async () => {
      try {
        const data = await getRecentUpdates();
        if (data) {
          setUpdates(data);
        }
      } catch (error) {
        console.error('Error loading recent updates:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRecentUpdates();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      await saveRecentUpdates(updates);
      setMessage("Recent updates saved successfully!");
      setIsEditing(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving recent updates:', error);
      setMessage("Failed to save recent updates. Please try again.");
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleAddUpdate = () => {
    if (newUpdate.app && newUpdate.version && newUpdate.changes[0]) {
      setUpdates(prev => [{ ...newUpdate, id: `temp-${Date.now()}` }, ...prev]);
      setNewUpdate({
        app: '',
        version: '',
        date: new Date().toISOString().split('T')[0],
        changes: ['']
      });
    }
  };

  const handleDeleteUpdate = (id) => {
    setUpdates(prev => prev.filter(update => update.id !== id));
  };

  const handleAddChange = (updateId) => {
    setUpdates(prev => prev.map(update => 
      update.id === updateId 
        ? { ...update, changes: [...update.changes, ''] }
        : update
    ));
  };

  const handleChangeUpdate = (updateId, changeIndex, value) => {
    setUpdates(prev => prev.map(update => 
      update.id === updateId 
        ? { 
            ...update, 
            changes: update.changes.map((change, idx) => 
              idx === changeIndex ? value : change
            )
          }
        : update
    ));
  };

  const handleRemoveChange = (updateId, changeIndex) => {
    setUpdates(prev => prev.map(update => 
      update.id === updateId 
        ? { 
            ...update, 
            changes: update.changes.filter((_, idx) => idx !== changeIndex)
          }
        : update
    ));
  };

  if (loading && !isEditing) {
    return (
      <div className="p-6 panel">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    // Show read-only version for non-admin users
    return (
      <div className="p-6 panel">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2 mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-stone-600" />
          Recent Updates
        </h3>
        <div className="space-y-4">
          {updates.slice(0, 3).map(update => (
            <div key={update.id} className="p-4 bg-paper rounded-xl border border-line">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">{update.app}</h4>
                  <p className="text-xs text-indigo-600">{update.version}</p>
                </div>
                <span className="text-xs text-gray-500">{update.date}</span>
              </div>
              <ul className="text-xs text-gray-600 space-y-1">
                {update.changes.map((change, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-indigo-500 mr-1">•</span>
                    <span>{change}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 panel">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-stone-600" />
          Recent Updates
        </h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Add New Update Form */}
          <div className="p-4 bg-paper rounded-xl border border-stone-200">
            <h4 className="font-semibold text-indigo-800 mb-3 flex items-center">
              <Plus className="w-4 h-4 mr-1" />
              Add New Update
            </h4>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                placeholder="App Name"
                value={newUpdate.app}
                onChange={(e) => setNewUpdate(prev => ({ ...prev, app: e.target.value }))}
                className="field"
              />
              <input
                type="text"
                placeholder="Version (e.g., v1.2.0)"
                value={newUpdate.version}
                onChange={(e) => setNewUpdate(prev => ({ ...prev, version: e.target.value }))}
                className="field"
              />
            </div>
            <input
              type="date"
              value={newUpdate.date}
              onChange={(e) => setNewUpdate(prev => ({ ...prev, date: e.target.value }))}
              className="field mb-3"
            />
            <div className="space-y-2 mb-3">
              <label className="text-sm font-medium text-indigo-800">Changes:</label>
              {newUpdate.changes.map((change, index) => (
                <div key={index} className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Describe the change"
                    value={change}
                    onChange={(e) => setNewUpdate(prev => ({
                      ...prev,
                      changes: prev.changes.map((c, i) => i === index ? e.target.value : c)
                    }))}
                    className="field flex-1"
                  />
                  {newUpdate.changes.length > 1 && (
                    <button
                      onClick={() => setNewUpdate(prev => ({
                        ...prev,
                        changes: prev.changes.filter((_, i) => i !== index)
                      }))}
                      className="p-2 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setNewUpdate(prev => ({ ...prev, changes: [...prev.changes, ''] }))}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                + Add another change
              </button>
            </div>
            <button
              onClick={handleAddUpdate}
              className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition"
            >
              Add Update
            </button>
          </div>

          {/* Existing Updates */}
          <div className="space-y-3">
            {updates.map(update => (
              <div key={update.id} className="p-3 bg-paper rounded-xl border border-line">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={update.app}
                      onChange={(e) => setUpdates(prev => prev.map(u => u.id === update.id ? { ...u, app: e.target.value } : u))}
                      className="font-semibold text-gray-900 text-sm bg-transparent border-none p-0 focus:ring-0"
                    />
                    <input
                      type="text"
                      value={update.version}
                      onChange={(e) => setUpdates(prev => prev.map(u => u.id === update.id ? { ...u, version: e.target.value } : u))}
                      className="text-xs text-indigo-600 bg-transparent border-none p-0 focus:ring-0"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <input
                      type="date"
                      value={update.date}
                      onChange={(e) => setUpdates(prev => prev.map(u => u.id === update.id ? { ...u, date: e.target.value } : u))}
                      className="field w-auto text-xs py-1"
                    />
                    <button
                      onClick={() => handleDeleteUpdate(update.id)}
                      className="p-1 text-red-500 hover:text-red-700 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  {update.changes.map((change, index) => (
                    <div key={index} className="flex space-x-2">
                      <span className="text-indigo-500 text-xs mt-1">•</span>
                      <input
                        type="text"
                        value={change}
                        onChange={(e) => handleChangeUpdate(update.id, index, e.target.value)}
                        className="text-xs text-gray-600 bg-transparent border-none p-0 focus:ring-0 flex-1"
                      />
                      {update.changes.length > 1 && (
                        <button
                          onClick={() => handleRemoveChange(update.id, index)}
                          className="p-1 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => handleAddChange(update.id)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 ml-3"
                  >
                    + Add change
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center space-x-1 px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {updates.slice(0, 3).map(update => (
            <div key={update.id} className="p-4 bg-paper rounded-xl border border-line">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">{update.app}</h4>
                  <p className="text-xs text-indigo-600">{update.version}</p>
                </div>
                <span className="text-xs text-gray-500">{update.date}</span>
              </div>
              <ul className="text-xs text-gray-600 space-y-1">
                {update.changes.map((change, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-indigo-500 mr-1">•</span>
                    <span>{change}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentUpdatesCard;
